/**
 * Тимчасові відмови API (UX-18, UX-19) без залежностей: їх беруть і клієнт API платформи
 * (`platform/api.ts`), і компоненти дизайн-системи (`ui/form`), тож модуль не тягне ні React, ні
 * клієнта. Помилку розпізнаємо за формою (`status`, `code`, `retryAfter` — як в `ApiProblem`).
 */

/** Що потрібно від помилки, щоб розпізнати тимчасову відмову. */
interface Temporary {
  status: number;
  code: string;
  retryAfter: number;
}

/**
 * Тимчасові відмови: запит НЕ виконано (транзакцію відкочено або сервер не дійшов до обробника),
 * повтор з тим самим ключем безпечний, а `Retry-After` каже, коли (api-writes.md, розд. 6;
 * api-and-ai.md, розд. 6.2a; черга IAM — висновок S5). Код звіряємо разом зі статусом. `locked`
 * (блокування входу) і `rate_limited` без `Retry-After` (квота листів IAM за годину) — не тут:
 * повтор за секунди там не допоможе.
 */
const TEMPORARY: Readonly<Record<string, number>> = {
  rate_limited: 429,
  too_many_requests: 429,
  busy: 429,
  heavy_busy: 429,
  record_busy: 409,
  idempotency_in_progress: 409,
  write_unavailable: 503,
  write_timeout: 503,
  schema_unavailable: 503,
  operation_timeout: 503,
};

/**
 * Тимчасові лише для читання. `503 operation_timeout` дає кореневий обробник api на будь-який
 * 57014, не знаючи, читання це чи запис, — зокрема після коміту на маршрутах без `Idempotency-Key`
 * (IAM, адмінка моделі, модулі). Для зміни це «невідомо, чи виконано»: ні автоматичного повтору,
 * ні поради «спробуйте через N с» (рев'ю Security-2 #289, Should 2).
 */
export const READ_ONLY_TEMPORARY: ReadonlySet<string> = new Set(['operation_timeout']);

/** Найдовше очікування, яке ще має сенс показувати як «повтор»: далі — «спробуйте пізніше». */
const MAX_RETRY_AFTER_S = 86_400;

/** `Retry-After` (RFC 9110): секунди чи HTTP-дата → секунд від `now`; хибне — `undefined`. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | undefined {
  const raw = value?.trim();
  if (!raw) return undefined;
  if (/^\d+$/.test(raw)) return Math.min(Number(raw), MAX_RETRY_AFTER_S);
  // HTTP-дата починається з дня тижня (усі три формати RFC 9110), інакше `Date.parse` візьме й «-1».
  const at = /^[A-Za-z]{3}/.test(raw) ? Date.parse(raw) : Number.NaN;
  if (Number.isNaN(at)) return undefined;
  return Math.min(Math.max(0, Math.ceil((at - now) / 1000)), MAX_RETRY_AFTER_S);
}

/** Секунд до повтору, якщо збій — тимчасова відмова з `Retry-After`; інакше `null`. */
export function retryAfterOf(error: unknown): number | null {
  const e = error as Partial<Temporary> | null;
  if (typeof e?.retryAfter !== 'number' || typeof e.code !== 'string') return null;
  return TEMPORARY[e.code] === e.status ? e.retryAfter : null;
}
